/**
 * 스캔 라벨링 데이터 업로드 — 인식 모델 학습용 수집.
 *
 * BE: ScanLabelController @ POST /api/v1/user/scan/labels (multipart, permitAll —
 *   FE 에서는 userBaseUrl 기준 상대경로 "/scan/labels" 로 부른다 —
 *   비회원 스캔 라벨도 수집한다. 토큰이 있으면 회원이 스탬프된다)
 *
 * 같은 촬영 1장은 labelKey(클라이언트 uuid)로 업서트된다 — 사용자가 핸들을 고쳐
 * 다시 매치하면 라벨/매칭 정보만 갱신되고 사진 파일은 최초 1회만 저장된다.
 * 좌표는 라벨 스키마 v3(rounded_quad_8_tangent_points, 원본 이미지 픽셀) 그대로 싣는다.
 */
import { uploadMultipart } from "./upload";

interface OutlinePoint {
  x: number;
  y: number;
}

/**
 * 촬영 조건 태그 (2026-08-18 추가). 학습 데이터에서 "슬리브 실패율" 같은 부분집합을
 * 뽑으려면 조건을 알아야 하는데, 종전 라벨엔 이 정보가 없어 **392장을 모아두고도
 * 슬리브가 몇 장인지 셀 수 없었다**. 사진만 쌓고 조건을 안 남기면 나중에 못 쓴다.
 */
export const SCAN_LABEL_CONDITIONS = ["sleeve", "toploader", "glare", "tilted"] as const;
export type ScanLabelCondition = (typeof SCAN_LABEL_CONDITIONS)[number];

export interface ScanLabelMeta {
  /** 촬영 1장 식별 uuid — 재매치 업서트 기준 */
  labelKey: string;
  imageWidth: number;
  imageHeight: number;
  /** 라벨 좌표 스키마 식별자 */
  schema: "rounded_quad_8_tangent_points_v3";
  /** 모델(테두리 인식) 기준선 8접점 — 검출 실패 촬영이면 null */
  modelOutline: OutlinePoint[] | null;
  /** 사용자 라벨링 8접점 */
  userOutline: OutlinePoint[];
  /** 스캔 언어 (매칭 인덱스 언어) */
  scanLang: string;
  /** 매칭 결과 — matcher 미가동이면 null */
  match: {
    matched: boolean;
    matches: { cardSerlno: number; similarity: number }[];
  } | null;
  /**
   * 사용자가 고른 정답 — **영역별로 따로** 기록한다 (2026-08-18).
   * 숫자면 그 cardSerlno 가 정답, "none" 이면 그 영역 후보엔 정답이 없음, null 은 미판정.
   *
   * ⚠ 하나로 합치면 안 된다. 모델 영역(모델이 잡은 테두리)과 내 영역(사용자가 고친 테두리)은
   * **후보 목록이 다르다** — "모델 영역은 못 찾았는데 내 영역은 찾았다"가 곧 검출 품질이
   * 매칭을 좌우한다는 증거이고, 그게 이 수집의 핵심 신호다. 합치면 그 구분이 사라진다.
   */
  verdicts?: {
    model: number | "none" | null;
    user: number | "none" | null;
  };
  /** 촬영 조건 태그 — 학습 부분집합 추출용 */
  conditions?: ScanLabelCondition[];
  /**
   * 이 사진이 어떻게 찍혔는가 (2026-08-18).
   *  - "auto":   자동 촬영 — 사진 = 라이브 **판정 프레임**(저해상, 실제 추론 입력 분포)
   *  - "manual": 수동 셔터 — 사진 = 고해상 스틸(외곽선 라벨·OCR 에 유리)
   * 둘은 해상도도 분포도 달라서 섞어서 학습·평가하면 결과를 해석할 수 없다.
   * 반드시 구분해서 남긴다.
   */
  captureMode?: "auto" | "manual";
  /**
   * 촬영 **순간**의 라이브 신호 스냅샷 (2026-08-18).
   *
   * 수동 촬영은 대개 "자동이 안 돼서 어쩔 수 없이" 누른 것이라 그 자체가 실패 사례인데,
   * captureMode 만으로는 **왜** 실패했는지 알 수 없다 — 검출이 못 따라온 것(가이드 밖·
   * 선명도 미달)과 매칭이 확신에 못 간 것(합의 미달)은 고칠 곳이 완전히 다르다.
   * 이 값들은 이미 화면에 띄우고 있던 것이라 추가 촬영·조작 없이 그대로 싣는다.
   *
   * 이렇게 모으면 임계값(sharpness 30 / vote 3 / sim 0.5)을 감이 아니라 **분포로**
   * 잡을 수 있다 — 예: 실패 표본의 sharpness 가 20~28 에 몰리면 임계 30 이 범인이다.
   */
  liveSignal?: {
    /** 프레임 합의 횟수 (AUTO_CAPTURE_VOTE 이상이면 매칭 게이트 통과) */
    vote: number;
    /** 라이브 top-1 유사도 — 힌트가 한 번도 안 왔으면 null */
    topSim: number | null;
    /** 마지막 검출 quad 의 선명도 — 엔진이 안 주면 null */
    sharpness: number | null;
    /**
     * 자동 셔터의 검출 상태: searching(미검출) / holding(검출됨).
     * "align"(가이드 밖·작음)은 가이드 기하 게이트가 있던 2026-08-20 이전 수집분에만
     * 존재한다 — 게이트 제거 후에는 나오지 않지만 과거 라벨 호환으로 타입에 남긴다.
     */
    alignStatus: "searching" | "align" | "holding";
    /** "○○ 인식됨"(확신 톤) 배지가 촬영 시점에 유효했나 */
    hadStableHint: boolean;
  } | null;
  /* 짝 이미지(고해상 스틸) `pair` 필드는 2026-08-20 스틸 제거와 함께 삭제됐다 —
     기존 수집분의 LABEL_JSON 에만 남아 있다. */
}

export interface ScanLabelResponse {
  scanLabelSerialNo: number;
}

export function uploadScanLabel(
  file: File,
  meta: ScanLabelMeta,
): Promise<ScanLabelResponse> {
  // ⚠ base(userBaseUrl) **이후 경로만** 넘긴다. 전체 경로를 넘겨 URL 이
  // ".../api/v1/user/api/v1/user/scan/labels" 로 중복돼 401 이 났고, 그래서 라벨이
  // 8/7 기능 추가 이후 **한 건도 올라가지 않았다**(2026-08-18 실기기 logcat 으로 확인).
  // 다른 업로드(/collection/photos, /inquiries/attachments)와 같은 규약이다.
  return uploadMultipart<ScanLabelResponse>("/scan/labels", file, {
    meta: JSON.stringify(meta),
  });
}
